import { useUpdateAgentConfigurationVersion } from '@/queries/agent-configuration-versions';
import EditOutlined from '@ant-design/icons/EditOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import Button from 'antd/es/button';
import message from 'antd/es/message';
import Tag from 'antd/es/tag';
import Typography from 'antd/es/typography';
import React, { useCallback, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { SchemaEditorModal } from './SchemaEditorModal';

const { Text } = Typography;

const SchemaCard = styled.div`
  ${({ theme }) => `
  background: ${theme.colorFillQuaternary};
  border: ${`${theme.lineWidth}px solid ${theme.colorBorder}`};
  border-radius: ${theme.borderRadius}px;
  padding: ${theme.padding}px;
  margin-bottom: ${theme.marginLG}px;
  transition: all ${theme.motionDurationMid}s;

  &:hover {
    border-color: ${theme.colorPrimary};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  .schema-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${theme.marginSM}px;
  }

  .schema-title {
    font-weight: ${theme.fontWeightStrong};
    font-size: ${theme.fontSize}px;
    color: ${theme.colorText};
  }

  .schema-meta {
    display: flex;
    gap: ${theme.marginXS}px;
    margin-bottom: ${theme.marginSM}px;
  }

  .schema-preview {
    font-family: ${theme.fontFamilyCode};
    font-size: ${theme.fontSizeSM}px;
    color: ${theme.colorTextSecondary};
    background: ${theme.colorBgContainer};
    padding: ${theme.paddingXS}px;
    border-radius: ${theme.borderRadiusSM}px;
    max-height: ${theme.controlHeightSM * 4.2}px;
    overflow: hidden;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: ${theme.controlHeightSM}px;
      background: linear-gradient(
        to bottom,
        transparent,
        ${theme.colorBgContainer}
      );
    }
  }
`}
`;

interface SchemasConfigSectionProps {
  config: {
    inputSchema?: Record<string, unknown> | null;
    outputSchema?: Record<string, unknown> | null;
  };
  versionId?: string;
}

// Helper to analyze schema structure
const analyzeSchema = (
  schema: Record<string, unknown> | null | undefined,
): { fieldCount: number; requiredCount: number; preview: string } => {
  if (!schema || typeof schema !== 'object') {
    return { fieldCount: 0, requiredCount: 0, preview: '' };
  }

  const properties = (schema.properties || {}) as Record<string, unknown>;
  const required = (schema.required || []) as string[];
  const fieldCount = Object.keys(properties).length;
  const requiredCount = required.length;
  const preview = JSON.stringify(schema, null, 2);

  return { fieldCount, requiredCount, preview };
};

export const SchemasConfigSection: React.FC<SchemasConfigSectionProps> = ({
  config,
  versionId,
}) => {
  const theme = useTheme();
  const updateVersionMutation = useUpdateAgentConfigurationVersion();
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [inputSchema, setInputSchema] = useState(config.inputSchema);
  const [outputSchema, setOutputSchema] = useState(config.outputSchema);

  const [schemaModalConfig, setSchemaModalConfig] = useState<{
    open: boolean;
    field: 'inputSchema' | 'outputSchema' | null;
    title: string;
    description: string;
  }>({
    open: false,
    field: null,
    title: '',
    description: '',
  });

  // Reset when config changes
  useEffect(() => {
    setInputSchema(config.inputSchema);
    setOutputSchema(config.outputSchema);
    setHasChanges(false);
  }, [config]);

  const openSchemaModal = useCallback(
    (
      field: 'inputSchema' | 'outputSchema',
      title: string,
      description: string,
    ) => {
      setSchemaModalConfig({
        open: true,
        field,
        title,
        description,
      });
    },
    [],
  );

  const handleSchemaModalSave = useCallback(
    (schema: Record<string, unknown> | null) => {
      if (schemaModalConfig.field === 'inputSchema') {
        setInputSchema(schema);
      } else if (schemaModalConfig.field === 'outputSchema') {
        setOutputSchema(schema);
      }
      setHasChanges(true);
      setSchemaModalConfig({
        open: false,
        field: null,
        title: '',
        description: '',
      });
    },
    [schemaModalConfig.field],
  );

  const handleSave = useCallback(async () => {
    if (!versionId || !hasChanges) return;

    setIsSaving(true);
    try {
      await updateVersionMutation.mutateAsync({
        versionId,
        data: {
          inputSchema: inputSchema ?? undefined,
          outputSchema: outputSchema ?? undefined,
        },
      });

      void message.success('Schemas saved successfully');
      setHasChanges(false);
    } catch (error) {
      void message.error('Failed to save schemas');
    } finally {
      setIsSaving(false);
    }
  }, [versionId, hasChanges, inputSchema, outputSchema, updateVersionMutation]);

  const renderSchemaCard = (
    field: 'inputSchema' | 'outputSchema',
    title: string,
    description: string,
    schema: Record<string, unknown> | null | undefined,
  ) => {
    const schemaInfo = analyzeSchema(schema);

    return (
      <SchemaCard>
        <div className="schema-header">
          <div className="schema-title">{title}</div>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openSchemaModal(field, title, description)}
          >
            Edit
          </Button>
        </div>
        <Text
          type="secondary"
          style={{ display: 'block', marginBottom: `${theme.marginXS}px` }}
        >
          {description}
        </Text>
        {schemaInfo.fieldCount > 0 ? (
          <>
            <div className="schema-meta">
              <Tag color="blue">{schemaInfo.fieldCount} fields</Tag>
              {schemaInfo.requiredCount > 0 && (
                <Tag color="orange">{schemaInfo.requiredCount} required</Tag>
              )}
            </div>
            <div className="schema-preview">
              <pre style={{ margin: 0 }}>{schemaInfo.preview}</pre>
            </div>
          </>
        ) : (
          <Text type="secondary" style={{ fontStyle: 'italic' }}>
            No schema defined
          </Text>
        )}
      </SchemaCard>
    );
  };

  return (
    <>
      {renderSchemaCard(
        'inputSchema',
        'Input Schema',
        'Define the expected input structure for this agent',
        inputSchema,
      )}

      {renderSchemaCard(
        'outputSchema',
        'Output Schema',
        'Define the output structure that this agent will produce',
        outputSchema,
      )}

      <div
        style={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#fff',
          padding: `${theme.padding}px 0`,
          borderTop: `${theme.lineWidth}px solid #f0f0f0`,
          marginTop: `${theme.marginLG}px`,
        }}
      >
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={isSaving}
          disabled={!versionId || !hasChanges}
          block
        >
          Save Changes
        </Button>
      </div>

      {schemaModalConfig.field && (
        <SchemaEditorModal
          open={schemaModalConfig.open}
          onClose={() =>
            setSchemaModalConfig({
              open: false,
              field: null,
              title: '',
              description: '',
            })
          }
          onSave={handleSchemaModalSave}
          schema={
            schemaModalConfig.field === 'inputSchema'
              ? (inputSchema ?? {})
              : (outputSchema ?? {})
          }
          title={schemaModalConfig.title}
        />
      )}
    </>
  );
};
